import { useEffect, useMemo, useRef } from "react";
import { bindX86EmbedCardLock150To200 } from "./lockEmbedCardBetween150And200";
import { Link, useNavigate } from "react-router-dom";
import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";

/** 笔记本首图 KV（2358×1054）：左文案 / 右屏，卡片平贴在屏内可用区；文件名须 ASCII，生产 CDN/Nginx 对中文路径会落到 SPA HTML */
import imgHeroCover from "@/assets/images/x86/x86-hero-cover.png";
import img2 from "@/assets/images/x86/x86-detail-2.png";
import iconUpdateDate from "@/assets/images/x86/icon-update-date.png";
import iconFileSize from "@/assets/images/x86/icon-file-size.png";
import iconVersion from "@/assets/images/x86/icon-version.png";
import dataJson from "@/../public/data.json";
import { getServiceVal } from "@/utils/cookies";
import { DESKTOP_X86_DOWNLOAD_SECTION_ID, FREE_DOWNLOAD_PATH, publicPath, BTN_X86_OBTAIN_TEXT, DESKTOP_X86_TRIAL_V500_KEY, X86_OBTAIN_PAGE_PATH } from "@/utils/base";
import { findDesktopPackageByDownloadKey, getAllDesktopDownloadGroups } from "@/utils/desktopDownloadPackage";
import {
  bindX86DownloadIntentAfterLogin,
  consumeAuthorizationTokenFromUrl,
} from "@/utils/desktopDownloadReport";

import "./index.less";

const prefix = "x86-page";

const X86_CARD_DEFAULT_TITLE = "KaihongOS桌面版（x86）5.0试用版安装镜像包";

function resolveX86CardTitle(title?: string): string {
  if (
    !title ||
    title === "KaihongOS桌面版(X86)5.0试用版" ||
    title === "KaihongOS桌面版(x86)5.0试用版" ||
    title === "KaihongOS桌面版（X86）5.0试用版" ||
    title === "KaihongOS桌面版（x86）5.0试用版" ||
    title === "KaihongOS桌面版（x86）5.0试用版镜像包" ||
    title === "KaihongOS 桌面版（x86）5.0试用版镜像包"
  ) {
    return X86_CARD_DEFAULT_TITLE;
  }
  return title;
}

/** 2026/04/30 → 2026/06/30（屏内卡片与设计稿一致，无空格） */
function formatUpdateDateDisplay(date: string): string {
  const parts = date.trim().split(/[/.-\s]/).filter(Boolean);
  if (parts.length >= 3) {
    const [y, mo, d] = parts;
    return `${y}/${mo.padStart(2, "0")}/${d.padStart(2, "0")}`;
  }
  return date;
}

/** 3.81GB → 3.81 GB */
function formatFileSizeDisplay(size: string): string {
  const trimmed = size.trim();
  const matched = trimmed.match(/^([\d.]+)\s*([A-Za-z]+)$/);
  if (matched) {
    return `${matched[1]} ${matched[2]}`;
  }
  return trimmed;
}

/** 嵌入卡正文用 Noto Sans SC（秀气、可读性好）；仅本页注入一次，失败则回落本地字体栈 */
const NOTO_SANS_SC_STYLESHEET_ID = "x86-page-noto-sans-sc";
const NOTO_SANS_SC_STYLESHEET_HREF =
  "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;600;700&display=swap";

/** 资料下载（站内路由，与官网跳转目标一致） */
const FREE_DOWNLOAD_TO = `${String(publicPath).replace(/\/$/, "")}${FREE_DOWNLOAD_PATH}#${DESKTOP_X86_DOWNLOAD_SECTION_ID}`;

/** 安装教程视频（站内课程页，与 developer.kaihong.com/videoCollection 一致） */
const X86_INSTALL_TUTORIAL_TO = `${String(publicPath).replace(/\/$/, "")}/videoCollection?id=2055086956257333250`;

const X86_OBTAIN_TO = `${String(publicPath).replace(/\/$/, "")}${X86_OBTAIN_PAGE_PATH}`;

type SectionItem = {
  src: string;
  alt: string;
};

/**
 * 图2 底部分别与旧图3/图4 像素对齐（2917×11206 上 MSE 匹配）。
 * 热区按整段高度覆盖，行为与原先「整图可点」一致。
 */
type DetailHotspot = {
  id: string;
  topPercent: number;
  heightPercent: number;
  ariaLabel: string;
  internalTo?: string;
  externalHref?: string;
};

const IMG2_DETAIL_HOTSPOTS: DetailHotspot[] = [
  {
    id: "free-download",
    topPercent: 78.8417,
    heightPercent: 14.4833,
    ariaLabel: "前往下载 KaihongOS 桌面版（x86）",
    internalTo: FREE_DOWNLOAD_TO,
  },
  {
    id: "install-tutorial",
    topPercent: 93.325,
    heightPercent: 6.675,
    ariaLabel: "安装教程视频",
    internalTo: X86_INSTALL_TUTORIAL_TO,
  },
];

const SECTION_IMAGES: SectionItem[] = [
  { src: imgHeroCover, alt: "KaihongOS桌面版（x86）" },
];

function renderDetailHotspot(hot: DetailHotspot) {
  const style = {
    top: `${hot.topPercent}%`,
    height: `${hot.heightPercent}%`,
  };
  const className = `${prefix}__hotspot`;
  if (hot.externalHref != null) {
    return (
      <a
        key={hot.id}
        className={className}
        style={style}
        href={hot.externalHref}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={hot.ariaLabel}
      />
    );
  }
  if (hot.internalTo != null) {
    return (
      <Link
        key={hot.id}
        className={className}
        style={style}
        to={hot.internalTo}
        aria-label={hot.ariaLabel}
      />
    );
  }
  return null;
}

function renderDownloadCardContent(
  desktopPackage: ReturnType<typeof findDesktopPackageByDownloadKey>,
  onCardObtain: () => void,
  opts?: { embedded?: boolean },
) {
  const embedded = opts?.embedded === true;
  const cardCls = embedded
    ? `${prefix}__downloadCard ${prefix}__downloadCard--embed`
    : `${prefix}__downloadCard`;

  const metaBlock = desktopPackage?.package_meta ? (
    <>
      <div className={`${prefix}__downloadCard-meta`}>
        <div className={`${prefix}__downloadCard-metaItem`}>
          {embedded ? (
            <img
              className={`${prefix}__downloadCard-metaIcon`}
              src={iconUpdateDate}
              alt=""
              draggable={false}
            />
          ) : null}
          <div className={`${prefix}__downloadCard-metaText`}>
            <span className={`${prefix}__downloadCard-metaLabel`}>
              {embedded ? "更新日期:" : "更新日期："}
            </span>
            <span className={`${prefix}__downloadCard-metaValue`}>
              {formatUpdateDateDisplay(
                desktopPackage.package_meta.release_date,
              )}
            </span>
          </div>
        </div>
        <div className={`${prefix}__downloadCard-metaItem`}>
          {embedded ? (
            <img
              className={`${prefix}__downloadCard-metaIcon`}
              src={iconFileSize}
              alt=""
              draggable={false}
            />
          ) : null}
          <div className={`${prefix}__downloadCard-metaText`}>
            <span className={`${prefix}__downloadCard-metaLabel`}>
              {embedded ? "文件大小:" : "文件大小："}
            </span>
            <span className={`${prefix}__downloadCard-metaValue`}>
              {formatFileSizeDisplay(desktopPackage.package_meta.size)}
            </span>
          </div>
        </div>
        <div className={`${prefix}__downloadCard-metaItem`}>
          {embedded ? (
            <img
              className={`${prefix}__downloadCard-metaIcon`}
              src={iconVersion}
              alt=""
              draggable={false}
            />
          ) : null}
          <div className={`${prefix}__downloadCard-metaText`}>
            <span className={`${prefix}__downloadCard-metaLabel`}>
              {embedded ? "版本号:" : "版本号："}
            </span>
            <span className={`${prefix}__downloadCard-metaValue`}>
              {desktopPackage.package_meta.version}
            </span>
          </div>
        </div>
      </div>
      <div className={`${prefix}__downloadCard-notes`}>
        <div className={`${prefix}__downloadCard-notesLabel`}>更新说明:</div>
        <p className={`${prefix}__downloadCard-notesText`}>
          {desktopPackage.package_meta.update_notes}
        </p>
      </div>
    </>
  ) : (
    <div
      className={`${prefix}__downloadCard-fallback`}
      dangerouslySetInnerHTML={{
        __html: desktopPackage?.describe ?? "",
      }}
    />
  );

  const cardBody = (
    <>
      <div className={`${prefix}__downloadCard-title`}>
        {resolveX86CardTitle(desktopPackage?.title)}
      </div>
      {metaBlock}
      <Button
        type="primary"
        size={embedded ? "large" : "middle"}
        icon={embedded ? <DownloadOutlined /> : undefined}
        className={`${prefix}__downloadCard-btn${embedded ? ` ${prefix}__downloadCard-btn--embed` : ""}`}
        onClick={onCardObtain}
        title={embedded ? "点击获取桌面版镜像包" : undefined}
      >
        <span className={`${prefix}__downloadCard-btnLabel`}>
          {BTN_X86_OBTAIN_TEXT}
        </span>
      </Button>
    </>
  );

  return (
    <div className={cardCls}>
      {embedded ? (
        <div className={`${prefix}__downloadCard-embedViewport`}>
          <div className={`${prefix}__downloadCard-embedBody`}>{cardBody}</div>
        </div>
      ) : (
        cardBody
      )}
    </div>
  );
}

function X86() {
  const { free_download } = dataJson;
  const navigate = useNavigate();
  const intentBindTriggeredRef = useRef(false);
  const pageRootRef = useRef<HTMLDivElement>(null);

  /**
   * 1) compact：仅真实窄屏切换下方通栏。
   * 2) 仅 (150%, 200%] 锁定信息卡片相对 150% 的比例（防自顾自增大截断）；
   *    ≤150% / >200% 不写样式；卡片 less 与底图逻辑保持原样。
   */
  useEffect(() => {
    const root = pageRootRef.current;
    if (!root) return;

    const compactQuery = window.matchMedia(
      "(max-width: 900px) and (max-device-width: 900px)",
    );
    const syncCompact = () => {
      root.classList.toggle("x86-page--compact", compactQuery.matches);
    };
    syncCompact();
    compactQuery.addEventListener("change", syncCompact);
    window.addEventListener("resize", syncCompact);

    const unbindCardLock = bindX86EmbedCardLock150To200(root);

    return () => {
      compactQuery.removeEventListener("change", syncCompact);
      window.removeEventListener("resize", syncCompact);
      root.classList.remove("x86-page--compact");
      unbindCardLock();
    };
  }, []);

  const desktopPackage = useMemo(
    () =>
      findDesktopPackageByDownloadKey(
        getAllDesktopDownloadGroups(),
        DESKTOP_X86_TRIAL_V500_KEY,
      ),
    [free_download.desktop_x86_version_download, free_download.desktop_version_download],
  );

  useEffect(() => {
    const prev = document.title;
    document.title = "KaihongOS桌面版（x86）";
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    if (document.getElementById(NOTO_SANS_SC_STYLESHEET_ID)) return;
    const preConnect = (href: string, crossOrigin?: "") => {
      const hint = `preconnect-${href.replace(/[^\w]/g, "")}`;
      if (document.querySelector(`link[data-x86-font="${hint}"]`)) return;
      const l = document.createElement("link");
      l.rel = "preconnect";
      l.href = href;
      if (crossOrigin !== undefined) l.crossOrigin = crossOrigin;
      l.dataset.x86Font = hint;
      document.head.appendChild(l);
    };
    preConnect("https://fonts.googleapis.com");
    preConnect("https://fonts.gstatic.com", "");
    const sheet = document.createElement("link");
    sheet.id = NOTO_SANS_SC_STYLESHEET_ID;
    sheet.rel = "stylesheet";
    sheet.href = NOTO_SANS_SC_STYLESHEET_HREF;
    document.head.appendChild(sheet);
  }, []);

  /** 与资料下载页一致：回流 token + 仅绑 X86 意向（未点下载时不记完整镜像参数） */
  useEffect(() => {
    consumeAuthorizationTokenFromUrl();
    if (!getServiceVal("Authorization")) return;
    if (intentBindTriggeredRef.current) return;
    intentBindTriggeredRef.current = true;
    void bindX86DownloadIntentAfterLogin().then((ok) => {
      if (!ok) {
        intentBindTriggeredRef.current = false;
      }
    });
  }, []);

  const onCardObtain = () => {
    navigate(X86_OBTAIN_TO);
  };

  const hero = SECTION_IMAGES[0];

  const cardBand = renderDownloadCardContent(desktopPackage, onCardObtain);

  const cardEmbed = renderDownloadCardContent(desktopPackage, onCardObtain, {
    embedded: true,
  });

  return (
    <div className={prefix} ref={pageRootRef}>
      <div className={`${prefix}__inner`}>
        <div className={`${prefix}__section ${prefix}__section--hero`}>
          <div className={`${prefix}__heroWrap`}>
            <img
              className={`${prefix}__heroImg`}
              src={hero.src}
              alt={hero.alt}
              draggable={false}
            />
            {/* 叠加层：百分比相对整图，对齐右侧屏内发光区（避开黑边与任务栏） */}
            <div className={`${prefix}__heroScreenLayer`}>
              <div className={`${prefix}__heroScreenSlot`}>
                <div className={`${prefix}__heroScreenPanel`}>{cardEmbed}</div>
              </div>
            </div>
          </div>
        </div>

        <div className={`${prefix}__section ${prefix}__section--download`}>
          <div className={`${prefix}__downloadBand`}>{cardBand}</div>
        </div>

        <div className={`${prefix}__section ${prefix}__section--body`}>
          <div className={`${prefix}__detailFig`}>
            <img
              className={`${prefix}__img`}
              src={img2}
              alt=""
              draggable={false}
            />
            {IMG2_DETAIL_HOTSPOTS.map(renderDetailHotspot)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default X86;
